import React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";

import ManageChats from "@/features/react-editor/components/ManageChats";
import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import { useWebContainer } from "@/features/web-container/context";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

function PreviewNavbar() {
  const { project } = useCurrentProjectContext();
  const navigate = useNavigate();
  const params = useParams();

  const funnelId = params.funnelId;

  const { logs } = useWebContainer();
  const [showLogs, setShowLogs] = React.useState(false);
  const shouldShowV2Debuggers =
    typeof window !== "undefined" &&
    localStorage.getItem("replo.debug.v2debuggers") === "true";
  const logsRef = React.useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to scroll to the bottom of the logs when they change
  React.useEffect(() => {
    if (logsRef.current) {
      logsRef.current?.scrollTo({
        top: logsRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  const handleBackNavigation = React.useCallback(() => {
    if (!project) {
      navigate("/shops");
      return;
    }

    if (funnelId) {
      navigate(`/shops/${project.id}/funnels/${funnelId}`);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(`/shops/${project.id}/site`);
  }, [navigate, project, funnelId]);

  return (
    <>
      <div className="flex gap-3 w-full">
        <div className="w-1/3">
          <div className="flex flex-row items-center gap-3">
            <IconButton
              variant="secondary"
              size="base"
              onClick={handleBackNavigation}
              icon={<ArrowLeft className="text-muted" size={16} />}
            />
            <div className="header-h3">
              {funnelId ? "Create New Page" : "Create New Funnel"}
            </div>
          </div>
        </div>

        <div className="w-2/3 flex items-center gap-3">
          <ResponsiveControls />
          {shouldShowV2Debuggers && (
            <>
              <ManageChats />
              <Button
                onClick={() => setShowLogs(!showLogs)}
                variant="secondary"
                size="base"
              >
                {showLogs ? "Hide WebContainer Logs" : "Show WebContainer Logs"}
              </Button>
            </>
          )}
        </div>
      </div>
      {showLogs && (
        <pre
          ref={logsRef}
          className={twMerge(
            // eslint-disable-next-line replo/no-tailwind-literal-color
            "fixed z-10 bottom-0 left-0 right-0 h-96 overflow-x-auto overflow-y-auto rounded bg-black p-2 text-xs text-green-400",
          )}
        >
          {logs.map((log, index) => (
            <div key={index}>
              {log.date.toLocaleTimeString()} {log.message}
            </div>
          ))}
        </pre>
      )}
    </>
  );
}

export default PreviewNavbar;
